import React, { useState } from "react";
import { GuaranteeBadge } from "./ConversionKit";

/* ============================================================
   TrustKit — infoproduct "packaging & proof" sections
   inspired by high-converting offer pages, Maca neo-brutalist style.
   ============================================================ */

const INK = "#141414";
const LIME = "#D9F154";
const COBALT = "#2F2BEB";
const WHITE = "#FFFFFF";
const BG = "#FAF7EF";
const RED = "#C2410C";

const MONO = { fontFamily: "'Archivo', sans-serif", fontSize: 10.5, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 800 };
const BLACK = "'Archivo Black', 'Archivo', sans-serif";

const Check = ({ color = INK, size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
);
const Cross = ({ color = RED, size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const Ext = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M8 7h9v9" /></svg>
);

const TK_CSS = `
  .tk-2col { }
  .tk-media-card { transition: transform .18s ease, box-shadow .18s ease; }
  .tk-media-card:hover { transform: translate(-2px,-2px); box-shadow: 8px 8px 0 #141414 !important; }
  .tk-cta { transition: transform .18s ease, box-shadow .18s ease; }
  .tk-cta:hover { transform: translate(2px,2px); box-shadow: 2px 2px 0 #141414 !important; }
  @media (max-width: 880px) {
    .tk-2col { grid-template-columns: 1fr !important; gap: 36px !important; }
    .tk-grid3 { grid-template-columns: 1fr !important; }
  }
  @media (max-width: 1100px) { .tk-grid4 { grid-template-columns: repeat(2, 1fr) !important; } }
  @media (max-width: 560px) { .tk-grid4 { grid-template-columns: 1fr !important; } }
`;

const MEDIA = [
  { outlet: "Forbes México", title: "PyMEs: 5 tips para que el crédito no sea tu enemigo", url: "https://forbes.com.mx/pymes-5-tips-para-convertir-al-credito-en-enemigo/" },
  { outlet: "Expansión", title: "9 errores al pedir un crédito para tu negocio", url: "https://expansion.mx/emprendedores/2014/10/02/9-errores-al-pedir-un-credito-para-tu-negocio" },
  { outlet: "El Economista", title: "Crecer duele y cuesta para emprendedores", url: "https://www.eleconomista.com.mx/el-empresario/Crecer-duele-y-cuesta-para-emprendedores-20141028-0232.html" },
];

export const MediaFeatures = ({ testid = "media-features" }) => (
  <section id="prensa" data-testid={testid} style={{ padding: "90px 40px", maxWidth: 1240, margin: "0 auto", position: "relative", zIndex: 5 }}>
    <style>{TK_CSS}</style>
    <div style={{ ...MONO, color: COBALT, marginBottom: 18 }}>— Han hablado de mí</div>
    <h2 style={{ fontFamily: BLACK, fontSize: "clamp(30px, 4vw, 54px)", letterSpacing: "-0.03em", lineHeight: 1.0, margin: 0, maxWidth: 780, color: INK }}>
      En los medios, <span style={{ color: COBALT }}>desde hace una década.</span>
    </h2>

    {/* Sivo credential band */}
    <div data-testid={`${testid}-sivo`} style={{ marginTop: 38, border: `2px solid ${INK}`, borderRadius: 18, boxShadow: `6px 6px 0 ${INK}`, background: COBALT, color: WHITE, padding: "30px 34px", display: "flex", gap: 30, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ fontFamily: BLACK, fontSize: "clamp(42px, 6vw, 70px)", color: LIME, lineHeight: 0.9, letterSpacing: "-0.03em" }}>US$100M</div>
      <div style={{ flex: 1, minWidth: 280 }}>
        <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.35 }}>Estructuré la facilidad de deuda de US$100M de Sivo para financiar PyMEs en Colombia y México.</div>
        <div style={{ ...MONO, fontSize: 10, marginTop: 12, opacity: 0.72 }}>Debt-as-a-Service · LATAM · Estructurador de la transacción</div>
      </div>
    </div>

    {/* Article cards */}
    <div className="tk-grid3" style={{ marginTop: 26, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
      {MEDIA.map((m, i) => (
        <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-article-${i}`}
          className="tk-media-card"
          style={{ textDecoration: "none", color: INK, border: `2px solid ${INK}`, borderRadius: 16, boxShadow: `5px 5px 0 ${INK}`, background: WHITE, padding: "26px 24px", display: "flex", flexDirection: "column", gap: 14, minHeight: 200 }}>
          <div style={{ fontFamily: BLACK, fontSize: 20, letterSpacing: "-0.02em" }}>{m.outlet}</div>
          <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35, flex: 1 }}>“{m.title}”</div>
          <div style={{ ...MONO, fontSize: 10, color: COBALT, display: "flex", alignItems: "center", gap: 8 }}>Leer el artículo <Ext size={13} /></div>
        </a>
      ))}
    </div>
  </section>
);

const INCLUDES = [
  "Ebook con el framework completo de deuda institucional",
  "Curso en video paso a paso, en español, para tu vertical",
  "Toolkit: plantillas de term sheet, cap table, loan tape y covenants",
  "The Debt Program: 8–10 semanas acompañado hasta tu term sheet",
  "Warm intros a +60 prestamistas y fondos curados",
  "Office hours en vivo conmigo cada semana",
  "Acceso inmediato tras el pago + actualizaciones de por vida",
  "Garantía de 14 días, sin preguntas · + bonos incluidos",
];

export const OfferStack = ({ testid = "offer-stack" }) => (
  <section data-testid={testid} style={{ padding: "90px 40px", background: WHITE, borderTop: "1px solid rgba(20,20,20,0.06)", borderBottom: "1px solid rgba(20,20,20,0.06)", position: "relative", zIndex: 5 }}>
    <style>{TK_CSS}</style>
    <div className="tk-2col" style={{ maxWidth: 1240, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "center" }}>
      <div style={{ border: `2px solid ${INK}`, borderRadius: 20, boxShadow: `7px 7px 0 ${INK}`, overflow: "hidden", background: BG }}>
        <img src="/product-stack.png" alt="Todo lo que recibes: ebook, curso, toolkit y bonos" style={{ width: "100%", display: "block" }} />
      </div>
      <div>
        <div style={{ ...MONO, color: COBALT, marginBottom: 16 }}>— Esto es TODO lo que recibes</div>
        <h2 style={{ fontFamily: BLACK, fontSize: "clamp(28px, 3.4vw, 46px)", letterSpacing: "-0.03em", lineHeight: 1.0, margin: "0 0 24px", color: INK }}>
          Un sistema completo, <span style={{ color: COBALT }}>no un PDF suelto.</span>
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
          {INCLUDES.map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 7, background: LIME, border: `2px solid ${INK}`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}><Check /></span>
              <span style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.4, color: INK }}>{t}</span>
            </div>
          ))}
        </div>
        <a href="#pricing" data-testid={`${testid}-cta`} className="tk-cta"
          style={{ marginTop: 30, display: "inline-block", background: LIME, color: INK, padding: "16px 28px", fontFamily: "'Archivo', sans-serif", fontSize: 12, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", border: `2px solid ${INK}`, borderRadius: 14, boxShadow: `4px 4px 0 ${INK}`, textDecoration: "none" }}>
          Ver programas y precios →
        </a>
      </div>
    </div>
  </section>
);

const BEFORE = [
  "Regalas 20–30% de equity por capital que no necesitabas",
  "Term sheets con covenants que no entiendes",
  "Meses tocando puertas sin una sola respuesta",
  "Costo de capital altísimo por no saber negociar",
  "Dependes de un banquero que no te explica nada",
];
const AFTER = [
  "Levantas deuda y cuidas tu cap table",
  "Lees y negocias cada covenant como insider",
  "Warm intros a +60 fondos que sí prestan a tu perfil",
  "Bajas tu costo de capital con un term sheet negociado",
  "Tú controlas el proceso, con un sistema claro",
];

const BARow = ({ text, dark }) => (
  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "9px 0" }}>
    <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", background: dark ? LIME : "transparent", border: `2px solid ${dark ? INK : RED}`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
      {dark ? <Check /> : <Cross />}
    </span>
    <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: dark ? WHITE : INK }}>{text}</span>
  </div>
);

export const BeforeAfter = ({ testid = "before-after" }) => (
  <section data-testid={testid} style={{ padding: "90px 40px", maxWidth: 1240, margin: "0 auto", position: "relative", zIndex: 5 }}>
    <style>{TK_CSS}</style>
    <div style={{ ...MONO, color: COBALT, marginBottom: 18 }}>— Antes / Después</div>
    <h2 style={{ fontFamily: BLACK, fontSize: "clamp(28px, 3.6vw, 50px)", letterSpacing: "-0.03em", lineHeight: 1.0, margin: "0 0 44px", color: INK, maxWidth: 820 }}>
      De pagar de más <span style={{ color: COBALT }}>a cerrar como insider.</span>
    </h2>
    <div className="tk-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
      <div style={{ border: `2px solid ${INK}`, borderRadius: 18, boxShadow: `5px 5px 0 ${INK}`, background: WHITE, padding: "30px 28px" }}>
        <div style={{ ...MONO, color: RED, marginBottom: 14 }}>Antes</div>
        {BEFORE.map((t, i) => <BARow key={i} text={t} />)}
      </div>
      <div style={{ border: `2px solid ${INK}`, borderRadius: 18, boxShadow: `5px 5px 0 ${INK}`, background: COBALT, padding: "30px 28px" }}>
        <div style={{ ...MONO, color: LIME, marginBottom: 14 }}>Después</div>
        {AFTER.map((t, i) => <BARow key={i} text={t} dark />)}
      </div>
    </div>
  </section>
);

export const GuaranteeSection = ({ testid = "guarantee-section" }) => (
  <section data-testid={testid} style={{ padding: "84px 40px", background: WHITE, borderTop: `2px solid ${INK}`, borderBottom: `2px solid ${INK}`, position: "relative", zIndex: 5 }}>
    <div style={{ maxWidth: 1040, margin: "0 auto", display: "flex", gap: 48, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
      <GuaranteeBadge title="14 días" subtitle="garantía" caption="sin preguntas" size={176} testid={`${testid}-badge`} />
      <div style={{ flex: 1, minWidth: 300 }}>
        <div style={{ ...MONO, color: INK, opacity: 0.6, marginBottom: 14 }}>— Todo por ganar, nada que perder</div>
        <h2 style={{ fontFamily: BLACK, fontSize: "clamp(28px, 3.6vw, 48px)", letterSpacing: "-0.03em", lineHeight: 1.0, margin: "0 0 18px", color: INK }}>
          Si no te sirve, <span style={{ color: COBALT }}>te devuelvo tu dinero.</span>
        </h2>
        <p style={{ fontSize: 17, lineHeight: 1.6, color: INK, fontWeight: 600, maxWidth: 640, margin: 0 }}>
          Entra, aplica el método y revisa los materiales. Si en 14 días sientes que no es para ti, me escribes y te regreso el 100% — sin preguntas, sin letra chica. Así de seguro estoy de lo que vas a lograr.
        </p>
      </div>
    </div>
  </section>
);

// ============================================================
// EBOOK LIBRARY — free lead magnet, one ebook per reader interest
// ============================================================
const PURPLE = "#6D5BD0";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EBOOK_OPTIONS = [
  { seg: "startup", label: "Para startups", title: "Primero el flujo. Luego la deuda.", desc: "Arma tu primera estructura de capital sin regalar tu empresa.", bg: COBALT, fg: WHITE, chip: LIME, chipInk: INK },
  { seg: "pyme", label: "Para PyMEs", title: "Primero el flujo. Luego la deuda.", desc: "De lo simple a lo sofisticado: ciclo operativo, factoraje, garantías y más.", bg: LIME, fg: INK, chip: INK, chipInk: WHITE },
  { seg: "institucion", label: "Institución financiera", title: "From Marks to Markets", desc: "El estado de la deuda privada en 2026: tamaño, riesgos y precio real.", bg: PURPLE, fg: WHITE, chip: LIME, chipInk: INK },
  { seg: "intangibles", label: "Intangibles", title: "Intangi-nomics", desc: "El sistema financiero para la economía del 90% que es intangible.", bg: INK, fg: WHITE, chip: LIME, chipInk: INK },
];

export const EbookLibrary = ({ testid = "ebook-library" }) => {
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const open = (opt) => { setActive(opt); setDone(null); setErr(""); setName(""); setEmail(""); };
  const close = () => { setActive(null); setSending(false); };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSending(true);
    try {
      const res = await fetch(`${API}/ebook/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, segment: active.seg, source: `ebook:${active.seg}` }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "No se pudo enviar. Intenta de nuevo.");
      setDone({ title: data.title, file: data.file });
    } catch (e2) {
      setErr(e2.message || "Error de red. Intenta de nuevo.");
    }
    setSending(false);
  };

  return (
    <section id="biblioteca" data-testid={testid} style={{ padding: "90px 40px", background: BG, borderTop: "1px solid rgba(20,20,20,0.06)", borderBottom: "1px solid rgba(20,20,20,0.06)", position: "relative", zIndex: 5 }}>
      <style>{TK_CSS}</style>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div style={{ ...MONO, color: COBALT, marginBottom: 16 }}>— Gratis · Biblioteca de ebooks</div>
        <h2 style={{ fontFamily: BLACK, fontSize: "clamp(30px, 4vw, 54px)", letterSpacing: "-0.03em", lineHeight: 1.0, margin: "0 0 14px", color: INK, maxWidth: 820 }}>
          Elige tu ebook. <span style={{ color: COBALT }}>Es gratis.</span>
        </h2>
        <p style={{ fontSize: 17, lineHeight: 1.55, color: INK, opacity: 0.8, maxWidth: 680, margin: "0 0 44px" }}>
          Deja tu correo y te mando el ebook según lo que estés levantando. Sin tarjeta, sin spam.
        </p>

        <div className="tk-grid4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 }}>
          {EBOOK_OPTIONS.map((o) => (
            <button key={o.seg} type="button" onClick={() => open(o)} data-testid={`ebook-card-${o.seg}`}
              className="tk-media-card"
              style={{ textAlign: "left", cursor: "pointer", border: `2px solid ${INK}`, borderRadius: 18, boxShadow: `6px 6px 0 ${INK}`, background: o.bg, color: o.fg, padding: "26px 24px", display: "flex", flexDirection: "column", gap: 14, minHeight: 280 }}>
              <span style={{ alignSelf: "flex-start", background: o.chip, color: o.chipInk, border: `2px solid ${INK}`, borderRadius: 999, padding: "5px 12px", fontFamily: "'Archivo', sans-serif", fontSize: 9.5, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>{o.label}</span>
              <span style={{ fontFamily: BLACK, fontSize: 21, letterSpacing: "-0.02em", lineHeight: 1.12, flex: "0 0 auto" }}>{o.title}</span>
              <span style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.45, opacity: 0.92, flex: 1 }}>{o.desc}</span>
              <span style={{ ...MONO, fontSize: 10, display: "flex", alignItems: "center", gap: 8 }}>Descargar gratis <span style={{ fontSize: 15 }}>→</span></span>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div data-testid="ebook-modal" onClick={close}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(20,20,20,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 460, background: WHITE, border: `2px solid ${INK}`, borderRadius: 20, boxShadow: `8px 8px 0 ${INK}`, padding: "34px 32px", position: "relative" }}>
            <button type="button" onClick={close} data-testid="ebook-close" aria-label="Cerrar"
              style={{ position: "absolute", top: 14, right: 16, background: "transparent", border: "none", fontSize: 24, cursor: "pointer", color: INK, lineHeight: 1 }}>×</button>

            {done ? (
              <div data-testid="ebook-success">
                <div style={{ ...MONO, color: COBALT, marginBottom: 12 }}>— ¡Listo!</div>
                <h3 style={{ fontFamily: BLACK, fontSize: 26, letterSpacing: "-0.02em", lineHeight: 1.05, margin: "0 0 12px", color: INK }}>Tu ebook está listo.</h3>
                <p style={{ fontSize: 15, lineHeight: 1.5, color: INK, opacity: 0.8, margin: "0 0 22px" }}>
                  Descarga «{done.title}» ahora. También te lo mandé a <strong>{email}</strong>.
                </p>
                <a href={`/ebooks/${done.file}`} download target="_blank" rel="noopener noreferrer" data-testid="ebook-download"
                  className="tk-cta"
                  style={{ display: "inline-block", background: LIME, color: INK, padding: "16px 28px", fontFamily: "'Archivo', sans-serif", fontSize: 12, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", border: `2px solid ${INK}`, borderRadius: 14, boxShadow: `4px 4px 0 ${INK}`, textDecoration: "none" }}>
                  Descargar ahora ↓
                </a>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="ebook-form">
                <div style={{ ...MONO, color: COBALT, marginBottom: 12 }}>— {active.label}</div>
                <h3 style={{ fontFamily: BLACK, fontSize: 24, letterSpacing: "-0.02em", lineHeight: 1.1, margin: "0 0 8px", color: INK }}>Recibe «{active.title}»</h3>
                <p style={{ fontSize: 14, lineHeight: 1.5, color: INK, opacity: 0.7, margin: "0 0 22px" }}>Déjame tu nombre y correo. Te llega al instante.</p>
                <input data-testid="ebook-name" type="text" required placeholder="Tu nombre" value={name} onChange={(e) => setName(e.target.value)} disabled={sending}
                  style={{ width: "100%", padding: "14px 16px", border: `2px solid ${INK}`, borderRadius: 12, fontSize: 15, fontFamily: "'Archivo', sans-serif", marginBottom: 12, outline: "none" }} />
                <input data-testid="ebook-email" type="email" required placeholder="tu@correo.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={sending}
                  style={{ width: "100%", padding: "14px 16px", border: `2px solid ${INK}`, borderRadius: 12, fontSize: 15, fontFamily: "'Archivo', sans-serif", marginBottom: 16, outline: "none" }} />
                <button type="submit" disabled={sending} data-testid="ebook-submit"
                  className="tk-cta"
                  style={{ width: "100%", background: COBALT, color: WHITE, padding: "16px 20px", fontFamily: "'Archivo', sans-serif", fontSize: 13, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", border: `2px solid ${INK}`, borderRadius: 14, boxShadow: `4px 4px 0 ${INK}`, cursor: "pointer" }}>
                  {sending ? "Enviando..." : "Enviar y descargar →"}
                </button>
                {err && <div data-testid="ebook-err" style={{ color: RED, fontWeight: 700, fontSize: 13, marginTop: 12 }}>{err}</div>}
                <div style={{ ...MONO, fontSize: 9, opacity: 0.5, marginTop: 14 }}>Sin tarjeta · Sin spam · Cancela cuando quieras</div>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
